import {TimeUtil} from './timeUtil.js';
import {sanitizeHtmlFromStoredText} from './browserStorageSanitizer.js';

/**
 * Handles creating dynamic elements
 * @type type
 */
export var ElementUtil = {
	
	/**
	 * This makes creating dynamic components much easier
	 * @param {Object} elementConfig (tag: '', ...attributes, children: [elementConfig..])
	 * @returns html element
	 */
	applyHtmlContent: function(element, html, trustedMarkup) {
		var text = html === null || html === undefined ? '' : String(html);
		if (trustedMarkup) {
			element.innerHTML = text;
		} else {
			element.textContent = sanitizeHtmlFromStoredText(text) || '';
		}
	},

	create: function(elementConfig) {
		var ElementUtil = this;
		
		var primaryElement = document.createElement(elementConfig.tag);	
		for (var key in elementConfig) {
			if (key !== 'tag' &&  
				key !== 'children' && 
				key !== 'listeners' &&
				key !== 'postinit' &&
				key !== 'innerHTML' &&
				key !== 'text' &&
				key !== 'trustedInnerHTML' &&
				key !== 'showLoading' && 
				key !== 'renderer' && 
				key !== 'dataProvider' && 
				key !== 'properties' && 	
				key !== 'defaults' &&
				key !== 'blankDateDefaultTime' &&
				key !== 'disable-shift-range-selection' &&
				key !== 'disableShiftRangeSelection' &&
				key !== 'disable-cell-text-selection' &&
				key !== 'disableCellTextSelection')
			primaryElement.setAttribute(key, elementConfig[key]);
		}
		
		for (var key in elementConfig) {
			if (key === 'renderer' || 
				key === 'dataProvider') {
				primaryElement[key] = elementConfig[key];
			}
		}
		
		if (elementConfig.properties) {
			for (var key in elementConfig.properties) {
				primaryElement[key] = elementConfig.properties[key];				
			}		
		}

		// Vaadin 25.2+ defaults uploadFormat to 'raw' (no FormData). App import/upload
		// endpoints expect multipart/form-data with a 'file' part, and many upload-request
		// handlers append extra fields to event.detail.formData.
		if (elementConfig.tag === 'vaadin-upload') {
			var uploadFormatExplicit = (elementConfig.properties && elementConfig.properties.uploadFormat !== undefined)
				|| elementConfig['upload-format'] !== undefined
				|| elementConfig.uploadFormat !== undefined;
			if (!uploadFormatExplicit) {
				primaryElement.uploadFormat = 'multipart';
			}
		}

		if (elementConfig.tag === 'vaadin-date-picker' || elementConfig.tag === 'vaadin-date-time-picker') {
			TimeUtil.applyDatePickerI18n(primaryElement);
		}

		if (elementConfig.tag === 'vaadin-date-time-picker' && elementConfig.blankDateDefaultTime !== undefined) {
			var defaultTime = elementConfig.blankDateDefaultTime === true ? '00:00:00' : elementConfig.blankDateDefaultTime;
			TimeUtil.initDateTimePickerBlankDateWithTime(primaryElement, defaultTime);
		}

		// Ext.js-style Shift+Click range selection for multi-select grids
		if (elementConfig.tag === 'vaadin-grid'
			&& elementConfig['disable-shift-range-selection'] !== true
			&& elementConfig.disableShiftRangeSelection !== true) {
			ElementUtil.enableShiftClickRangeSelection(primaryElement);
		}

		if (elementConfig.tag === 'vaadin-grid'
			&& elementConfig['disable-cell-text-selection'] !== true
			&& elementConfig.disableCellTextSelection !== true) {
			ElementUtil.enableCellTextSelection(primaryElement);
		}
		
		if (elementConfig.text !== undefined && elementConfig.text !== null) {
			primaryElement.textContent = String(elementConfig.text);
		} else if (elementConfig.innerHTML !== undefined && elementConfig.innerHTML !== null) {
			ElementUtil.applyHtmlContent(primaryElement, elementConfig.innerHTML, elementConfig.trustedInnerHTML === true);
		}
		
		primaryElement.showLoading = function(message) {
			var loadMask;
			if (primaryElement.shadowRoot) {
				loadMask = ElementUtil.loadMask(primaryElement.shadowRoot, message);
			} else {
				loadMask = ElementUtil.loadMask(primaryElement, message);
			}
			return loadMask;
		};
		
		if (elementConfig.listeners) {
			for (var eventName in elementConfig.listeners) {
				//if it object then handle buffering/options
				if (ElementUtil.isObject(elementConfig.listeners[eventName])) {
					var config = elementConfig.listeners[eventName];
					if (config.buffer) {
						
						var eventWrapper = function(e) {
							if (config.bufferedEvent) {
								clearInterval(config.bufferedEvent);
							}
							
							var wrapper = function() {
								try {
									if (config.scope) {
										config.fn.apply(config.scope, e);
									} else {
										config.fn(e);
									}
								} finally {
									clearInterval(config.bufferedEvent);
								}
							};
							config.bufferedEvent = setInterval(wrapper, config.buffer);
						};						
						
						primaryElement.addEventListener(eventName, eventWrapper);
					} else {
						primaryElement.addEventListener(eventName, config.fn);
					}
				} else {
					primaryElement.addEventListener(eventName, elementConfig.listeners[eventName]);
				}
			}
		}
		
		if (elementConfig.children && Array.isArray(elementConfig.children)) {
			var addChildren = function(children) {
				children.forEach(function(child){
					if (Array.isArray(child)) {
						addChildren(child);						
					} else {
						if (child.tagName) {
							primaryElement.append(child);
						} else {
							if (elementConfig.defaults) {
								child = Object.assign({}, elementConfig.defaults, child);
							}					
							primaryElement.append(ElementUtil.create(child));							
						}
					}
				});
			};
			addChildren(elementConfig.children);
		}
		
		if (elementConfig.postinit) {
			elementConfig.postinit(primaryElement);
		}
		
		return primaryElement;
	},
	
    isObject: (Object.prototype.toString.call(null) === '[object Object]') ?
        function(value) {
            // check ownerDocument here as well to exclude DOM nodes 
            return value !== null && value !== undefined && Object.prototype.toString.call(value) === '[object Object]' && value.ownerDocument === undefined;
        } :
        function(value) {
            return Object.prototype.toString.call(value) === '[object Object]';
    },	
	
	isSimpleObject: function(value) {
        return value instanceof Object && value.constructor === Object;
    },	
	
	makeMenuIcon: function (img, txt, attributes) {
		const item = document.createElement('vaadin-context-menu-item');
		var icon = null;
		if (img.includes('lumo')) {
			icon = document.createElement('vaadin-icon');
		} else {
			icon = document.createElement('vaadin-icon');
		}
		icon.setAttribute('icon', img);
		item.appendChild(icon);
		if (txt) {
			item.appendChild(document.createTextNode(txt));
		}
		if (attributes) {
			for (var key in attributes) {
				item.setAttribute(key, attributes[key]);
			}
		}
		return item;
	},	
	
	makeIcon: function (img, txt, attributes) {
		const item = document.createElement('span');
		var icon = document.createElement('vaadin-icon');
		icon.setAttribute('icon', img);
		icon.setAttribute('slot', 'prefix');
		icon.style.marginRight = '5px';
		item.appendChild(icon);
		if (txt) {
			item.appendChild(document.createTextNode(txt));
		}
		if (attributes) {
			for (var key in attributes) {
				item.setAttribute(key, attributes[key]);
			}
		}
		return item;
	},
	
	loadMask: function(element, loadingMessage) {
		var ElementUtil = this;
		loadingMessage = loadingMessage || 'Loading...';

		var mask = ElementUtil.create({
			tag: 'div',
			'class': 'load-mask', 
			children: [
				{
					tag: 'style',
					text: `
						.lds-dual-ring {
							display: inline-block;
							width: 64px;
							height: 64px;
						}
						.lds-dual-ring:after {
							content: " ";
							display: block;
							width: 46px;
							height: 46px;
							margin: 1px;
							border-radius: 50%;
							border: 5px solid #fff;
							border-color: #e41736 transparent #383636 transparent;
							animation: lds-dual-ring 1.2s linear infinite;
						}
						@keyframes lds-dual-ring {
							0% {
								transform: rotate(0deg);
							}
							100% {
								transform: rotate(360deg);	
							}
						}
						.load-mask {
							position: absolute;
							top: 0px;
							left: 0px;
							bottom: 0px;
							right: 0px;
							color: white;
							text-align: center;
							vertical-align: middle;
							align-content: center;
							background-color: rgba(0,0,0, 0.5);
							backdrop-filter: blur(2px);
						}
						.load-mask-inner {
							top: 40%;
							position: absolute;
							left: 47%;
							font-size: 21px;
						}
					`					
				},
				{
					tag: 'div',
					'class': 'load-mask-inner', 
					children: [
						{
							tag: 'table',
							children: [
								{
									tag: 'tr',
									children: [
										{
											tag: 'td',
											children: [
												{
													tag: 'span',
													'class': 'lds-dual-ring'
												}
											]
										},
										{
											tag: 'td',
											children: [
												{
													tag: 'p',
													text: loadingMessage
												}
											]											
										}
									]
								}
							]
						}
					]
				}
			]
		});
		
		element.append(mask);					

		return mask;
	},
	
	maskElement: function(element) {
		var ElementUtil = this;
		
		var mask = ElementUtil.create({
			tag: 'div',
			'class': 'load-mask', 
			children: [
				{
					tag: 'style',
					text: `
						.load-mask {
							position: absolute;
							top: 0px;
							left: 0px;
							bottom: 0px;
							right: 0px;
							color: white;
							text-align: center;
							vertical-align: middle;
							align-content: center;
							background-color: rgba(0,0,0, 0.5);
							backdrop-filter: blur(2px);
						}
						.load-mask-inner {
							top: 40%;
							position: absolute;
							left: 47%;
							font-size: 21px;
						}
					`					
				},
				{
					tag: 'div',
					'class': 'load-mask-inner', 
					children: [						
					]
				}
			]
		});
		
		element.append(mask);					

		return mask;
	},
	
	query: function(element, queryText) {
		var found;
		if (!element) {
			console.log("Parent element does not exist");
			return found;
		}
		//check for item id
		try {
			found = element.querySelector('[itemid=' + queryText + ']');
		} catch (e){			
		}
		
		if (!found) {
			try {
				found = element.querySelector('[itemId=' + queryText + ']');
			} catch (e){			
			}
		}
		
		//check shadow root
		if (!found && element.shadowRoot) {
			found = element.shadowRoot.querySelector(queryText);
			if (!found) {
				found = element.shadowRoot.querySelector('[itemid=' + queryText + ']');
			}
			if (!found) {
				found = element.shadowRoot.querySelector('[itemId=' + queryText + ']');
			}
		}
			
		//check normal root
		if (!found) {
			found = element.querySelector(queryText);
		}
		
		return found;
	},
	
	enableDisableAction: function(contextElement, elementId, state) {
		var button = ElementUtil.query(contextElement, elementId);
		if (button) {
			button.disabled = state;
		}
	},
	
	nextId: function() {
		var ElementUtil = this;
		ElementUtil.uniqueId = ElementUtil.uniqueId || 0;
		ElementUtil.uniqueId++;
		return 'DL-' + ElementUtil.uniqueId;		
	},
	
	/**
	 * take in "a.b" used in a complex form and translate to object filling in
	 * objects as need
	 * @param {type} originData
	 * @returns { a: { b: ...} }
	 */
	dotNotationToObj: function(originData) {
		var unpacked = {};
							
		for (var key in originData) {
			var parts = key.split('.');

			var partCount = 0;
			function indexObject(obj,name) {
				partCount++;
				if (!obj[name]) {
					obj[name] = {};
				};
				if (partCount === parts.length) {
					obj[name] = originData[key];
				}
				return obj[name];
			};
			var unpackPart = unpacked;
			for (var j=0; j<parts.length; j++) {
				unpackPart = indexObject(unpackPart, parts[j]);
			}								
		}
		return unpacked; 
	},
	
	/**
	 * Packs {a: { b: true } } to a.b = true;
	 *  
	 * @param {type} originData
	 * @returns {undefined}
	 */
	objToDotNotation: function(originData) {
		
		var res = {};
		(function recurse(obj, current) {
		  for(var key in obj) {
			var value = obj[key];
			var newKey = (current ? current + "." + key : key);  // joined key with dot
			if(value && typeof value === "object") {
			  recurse(value, newKey);  // it's a nested object, so do it again
			} else if (value && Array.isArray(value)) {
				value.forEach(function(element){
					recurse(element, newKey); 
				});
			} else {
			  res[newKey] = value;  // it's not an object, so set the property
			}
		  }
		})(originData);
		return res;		
	},
	
	removeAllChildren: function(parent) {
		while (parent.firstChild) {
			parent.removeChild(parent.firstChild);
		}
	},
	
	addElements: function(parent, children) {
		const fragment = document.createDocumentFragment();
		children.forEach(element => {
			fragment.appendChild(element); // Append each element to the fragment
		});
		parent.appendChild(fragment);
	},	
	
	isEmpty(object) {
		return object && Object.keys(object).length === 0 && object.constructor === Object;
	},
	
	findInputForCombo(comboElement) {
		var inputElement = null;
		Array.from(comboElement.childNodes).forEach(function(child){
			if (child.tagName === 'INPUT') {
				inputElement = child;
			}
		});				
		return inputElement;		
	},

	/**
	 * Resolves typed text from a vaadin-combo-box input event for server-side unit lookup.
	 * Prefers the event target value (same as toolbar filters), with light-DOM input fallback.
	 */
	resolveComboInputValue(event, comboElement) {
		var lookupValue = event && event.target ? event.target.value : null;
		if (lookupValue == null && comboElement) {
			var inputElement = ElementUtil.findInputForCombo(comboElement);
			lookupValue = inputElement ? inputElement.value : null;
		}
		return lookupValue;
	},

	/**
	 * Enables Ext.js-style Shift+Click range selection on a vaadin-grid.
	 * Requires a selection column (typically with auto-select). Uses the
	 * item-toggle event so the clicked row's selected state is applied
	 * to every row between the prior anchor and the clicked row.
	 * The anchor is only updated on non-Shift toggles (Ext.js behavior).
	 *
	 * Opt out when creating a grid via ElementUtil.create with
	 * disable-shift-range-selection: true (or disableShiftRangeSelection: true).
	 *
	 * @param {HTMLElement} grid vaadin-grid element
	 */
	enableShiftClickRangeSelection: function(grid) {
		if (!grid || grid.__shiftRangeSelectionEnabled) {
			return;
		}
		grid.__shiftRangeSelectionEnabled = true;

		var rangeStartItem = null;

		grid.addEventListener('item-toggle', function(event) {
			var item = event.detail.item;
			var selected = event.detail.selected;
			var shiftKey = event.detail.shiftKey;

			if (shiftKey && rangeStartItem) {
				var items = Array.isArray(grid.items) ? grid.items : null;
				if (items) {
					var startIndex = items.indexOf(rangeStartItem);
					var endIndex = items.indexOf(item);
					if (startIndex >= 0 && endIndex >= 0) {
						var fromIndex = Math.min(startIndex, endIndex);
						var toIndex = Math.max(startIndex, endIndex);
						for (var index = fromIndex; index <= toIndex; index++) {
							if (selected) {
								grid.selectItem(items[index]);
							} else {
								grid.deselectItem(items[index]);
							}
						}
						// Keep the original anchor while Shift is held (Ext.js-style)
						return;
					}
				}
			}

			rangeStartItem = item;
		});
	},

	/**
	 * Allows drag-to-select and copy of cell text without row selection clearing the highlight.
	 * Reverts row toggles from auto-select when the pointer moved enough to select text.
	 *
	 * Opt out when creating a grid via ElementUtil.create with
	 * disable-cell-text-selection: true (or disableCellTextSelection: true).
	 *
	 * @param {HTMLElement} grid vaadin-grid element
	 */
	enableCellTextSelection: function(grid) {
		if (!grid || grid.__cellTextSelectionEnabled) {
			return;
		}
		grid.__cellTextSelectionEnabled = true;

		var dragThresholdPixels = 4;
		var pointerDown = null;

		var hasNonEmptyTextSelection = function() {
			var selection = window.getSelection();
			return selection && selection.toString().length > 0;
		};

		var shouldPreserveTextSelection = function() {
			return grid.__cellTextSelectGesture === true || hasNonEmptyTextSelection();
		};

		var clearTextSelectGesture = function() {
			requestAnimationFrame(function() {
				grid.__cellTextSelectGesture = false;
				pointerDown = null;
			});
		};

		grid.addEventListener('pointerdown', function(event) {
			if (event.button !== 0) {
				return;
			}
			pointerDown = { x: event.clientX, y: event.clientY };
			grid.__cellTextSelectGesture = false;
		}, true);

		grid.addEventListener('pointermove', function(event) {
			if (!pointerDown || (event.buttons & 1) === 0) {
				return;
			}
			var deltaX = event.clientX - pointerDown.x;
			var deltaY = event.clientY - pointerDown.y;
			if (Math.hypot(deltaX, deltaY) > dragThresholdPixels) {
				grid.__cellTextSelectGesture = true;
			}
		}, true);

		grid.addEventListener('pointerup', function(event) {
			if (event.button !== 0) {
				return;
			}
			setTimeout(clearTextSelectGesture, 0);
		}, true);

		grid.addEventListener('pointercancel', clearTextSelectGesture, true);

		grid.addEventListener('item-toggle', function(event) {
			if (grid.__revertingSelectionForText || !shouldPreserveTextSelection()) {
				return;
			}

			var item = event.detail.item;
			var selected = event.detail.selected;
			grid.__revertingSelectionForText = true;
			try {
				if (selected) {
					grid.deselectItem(item);
				} else {
					grid.selectItem(item);
				}
			} finally {
				grid.__revertingSelectionForText = false;
			}
		});
	}
	
};


